import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { ConfigProvider } from 'antd';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { LOGIN_PATH, NO_INITIAL_STATE_PATHS } from '@/constants';
import { getMyself } from '@/services/admin';
import { getLevelList, getRegionList } from '@/services/base';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.AdminProfileSchema;
  regions?: API.RegionResponseSchema[];
  regionOptions?: Array<{ label: string; value: number }>;
  regionValueEnum?: Record<number, { text: string }>;
  levels?: API.LevelResponseSchema[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.AdminProfileSchema | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const res = await getMyself({
        skipErrorHandler: true,
      });
      return res.data;
    } catch (_error) {
      history.push(LOGIN_PATH);
    }
    return undefined;
  };

  const fetchBaseData = async () => {
    try {
      const [regionsRes, levelsRes] = await Promise.all([
        getRegionList({
          skipErrorHandler: true,
        }),
        getLevelList(
          {
            index: 1,
            limit: 100,
          },
          {
            skipErrorHandler: true,
          },
        ),
      ]);
      const regions = regionsRes.data || [];

      // 生成地区下拉框数据
      const regionOptions = regions.map((region) => ({
        label: region.name_cn,
        value: region.id,
      }));

      const regionValueEnum = regions.reduce(
        (acc, region) => {
          acc[region.id] = { text: region.name_cn };
          return acc;
        },
        {} as Record<number, { text: string }>,
      );

      return {
        regions,
        regionOptions,
        regionValueEnum,
        levels: levelsRes.data?.items || [],
      };
    } catch (_error) {
      console.warn('Failed to fetch base data:', _error);
      return {
        regions: [],
        regionOptions: [],
        regionValueEnum: {},
        levels: [],
      };
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (!NO_INITIAL_STATE_PATHS.includes(location.pathname)) {
    const [currentUser, baseData] = await Promise.all([
      fetchUserInfo(),
      fetchBaseData(),
    ]);

    return {
      fetchUserInfo,
      currentUser,
      regions: baseData.regions,
      regionOptions: baseData.regionOptions,
      regionValueEnum: baseData.regionValueEnum,
      levels: baseData.levels,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar_url,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== LOGIN_PATH) {
        history.push(LOGIN_PATH);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider
          modal={{
            centered: true,
          }}
        >
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

// 环境与接口地址映射
const envBaseURLMap = {
  dev: 'https://admin.tigeresim.top',
  prod: 'https://admin.tigeresims.com',
};

const getBaseURL = () => {
  /* const host = window.location.host;
  if (host.includes('admin.tigeresim.top')) {
    return envBaseURLMap.dev;
  }
  if (host.includes('admin.tigeresims.com')) {
    return envBaseURLMap.prod;
  }
  return envBaseURLMap.dev; */
  return '';
};

console.log('isDev', isDev);
console.log('baseURL', getBaseURL());

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
console.log('isDev', isDev);
export const request: RequestConfig = {
  // 本地开发时：'' (使用本地代理 /api/)
  baseURL: isDev ? '' : getBaseURL(),
  ...errorConfig,
};
